import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

import { Store } from '@ngrx/store';
import { StepperModule } from 'primeng/stepper';
import { firstValueFrom, map, skipWhile, take } from 'rxjs';
import { AuthState, selectActiveAccount, selectHasRoles } from '../../core/auth/auth.reducer';
import { Country } from './models/country.model';
import { NotificationSettings } from './notification-settings.model';
import { UserSettingsService } from './user-settings.service';

@Component({
  selector: 'carehub-user-settings',
  standalone: true,
  imports: [
    ButtonModule,
    CardModule,
    DividerModule,
    MultiSelectModule,
    PanelModule,
    StepperModule,
    ToggleSwitchModule,
    ReactiveFormsModule,
  ],
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
})
export class UserSettingsComponent implements OnInit {
  availableCountries: Country[] = [];

  notificationSettingsGroup: FormGroup;
  notificationSettingsId = signal<number>(0);

  marketsSettingsGroup: FormGroup;
  marketsSettingsId = signal<number>(0);
  showMarketsSettings = signal<boolean>(false);

  constructor(
    private fb: FormBuilder,
    private settingsService: UserSettingsService,
    private store: Store<AuthState>
  ) {
    this.notificationSettingsGroup = this.fb.group({
      isEmailEnabled: [false, Validators.required],
      isSmsEnabled: [false, [Validators.required]],
    });

    this.marketsSettingsGroup = this.fb.group({
      operatingCountryIds: [[], Validators.required],
    });
  }

  async ngOnInit(): Promise<void> {
    const hasMarketsAccess: boolean | undefined = await firstValueFrom(
      this.store.select(selectHasRoles(['SuperAdmin', 'Markets.Admin', 'Markets.Purchase'])).pipe(
        skipWhile(hasAccess => hasAccess === undefined),
        take(1)
      )
    );
    this.showMarketsSettings.set(!!hasMarketsAccess);

    this.fetchSettings();

    if (hasMarketsAccess) {
      this.fetchMarketsSettings();
    }
  }

  async save(): Promise<void> {
    const settings: NotificationSettings = await this.getUserInput();
    if (this.notificationSettingsId() !== 0) {
      this.updateSettings(settings);
    } else {
      this.createSettings(settings);
    }
  }

  private async createSettings(settings: NotificationSettings): Promise<void> {
    await this.settingsService.createNotificationSettings(settings);
  }

  private async updateSettings(settings: NotificationSettings): Promise<void> {
    await this.settingsService.updateNotificationSettings(settings);
  }

  private async getUserInput(): Promise<NotificationSettings> {
    const settings: NotificationSettings = this.notificationSettingsGroup.value;
    const accountName: string = await firstValueFrom(
      this.store.select(selectActiveAccount).pipe(
        skipWhile(account => account === null || account === undefined),
        map(account => account!.name)
      )
    );
    settings.user = accountName;
    settings.id = this.notificationSettingsId();

    return settings;
  }

  private async fetchSettings(): Promise<void> {
    const accountName: string = await firstValueFrom(
      this.store.select(selectActiveAccount).pipe(
        skipWhile(account => account === null || account === undefined),
        map(account => account!.name)
      )
    );
    const settings: NotificationSettings =
      await this.settingsService.getNotificationSettingsByUser(accountName);

    if (settings != null) {
      this.notificationSettingsGroup.patchValue(settings);
      this.notificationSettingsId.set(settings.id);
    }
  }

  async saveMarketsSettings(): Promise<void> {
    const payload: { operatingCountryIds: number[] } = {
      operatingCountryIds: this.marketsSettingsGroup.value.operatingCountryIds,
    };
    if (this.marketsSettingsId() !== 0) {
      await this.settingsService.updateUserMarketsSettings(payload);
    } else {
      const created: { id: number } = await this.settingsService.createUserMarketsSettings(payload);
      this.marketsSettingsId.set(created.id);
    }
  }

  private async fetchMarketsSettings(): Promise<void> {
    this.settingsService.getCountries().subscribe((countries) => {
      this.availableCountries = countries;
    });

    const settings: { id: number; operatingCountries: Country[] } | null =
      await this.settingsService.getUserMarketsSettings();
    if (settings != null) {
      this.marketsSettingsGroup.patchValue({
        operatingCountryIds: settings.operatingCountries.map(country => country.id),
      });
      this.marketsSettingsId.set(settings.id);
    }
  }
}
